import { useDraggable, useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useContext } from 'react';
import { ChevronDown, ChevronUp, GripVertical, Trash2, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  countParticipantAssignments,
  getStoreSlotOrder,
  formatParticipantDisplayName,
  getParticipantContactLine,
  getResolvedSlotAssignments,
  participantMatchesSlot,
} from '../../lib/events';
import { CardConfigContext } from './CardConfigContext';
import { TooltipIconButton } from '../ui/tooltip-icon-button';

function ParticipantCard({
  participant,
  onRemove,
  compact = true,
  dragHandleProps,
  showGrip = false,
  assignmentCount = 0,
  selected = false,
  onSelect,
}) {
  const cardConfig = useContext(CardConfigContext);
  const shown = new Set(cardConfig.fields);
  const displayName = formatParticipantDisplayName(
    shown.has('surname') ? participant : { ...participant, surname: '' }
  );
  const contactLine = getParticipantContactLine({
    phone: shown.has('phone') ? participant.phone : '',
    email: shown.has('email') ? participant.email : '',
  });
  const availabilitySlots = shown.has('availability') ? participant.availabilitySlots || [] : [];
  const extraLines = cardConfig.fields
    .filter((key) => key.startsWith('extra:'))
    .map((key) => {
      const label = key.slice('extra:'.length);
      return { label, value: participant.extras?.[label] };
    })
    .filter((line) => line.value);
  const dragClassName = dragHandleProps?.className;
  const dragRest = dragHandleProps
    ? Object.fromEntries(Object.entries(dragHandleProps).filter(([key]) => key !== 'className'))
    : null;

  return (
    <div
      className={cn(
        'flex w-full min-w-0 items-start gap-1.5 rounded-lg border border-[var(--ba-surface-border)] bg-[var(--ba-surface-bg)] p-2 shadow-sm',
        'min-h-[3.25rem]',
        compact ? 'text-xs' : 'text-sm',
        selected && 'ring-2 ring-brand-orange',
        onSelect && 'cursor-pointer'
      )}
      onClick={onSelect}
      onKeyDown={
        onSelect
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect();
              }
            }
          : undefined
      }
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
    >
      {showGrip && (
        <button
          type="button"
          className={cn(
            'mt-0.5 shrink-0 touch-none rounded p-0.5 text-[var(--ba-muted)] hover:text-brand-orange',
            dragClassName
          )}
          aria-label="Trascina partecipante"
          onClick={(event) => event.stopPropagation()}
          {...(dragRest || {})}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}
      <div
        {...(!showGrip ? dragRest : {})}
        className={cn('min-w-0 flex-1', !showGrip && dragClassName)}
      >
        <div className="flex items-center gap-1">
          <p className="min-w-0 flex-1 truncate font-semibold leading-tight">{displayName}</p>
          {shown.has('assignments') && assignmentCount > 0 && (
            <span className="shrink-0 rounded-full bg-brand-green/15 px-1.5 py-0.5 text-[9px] font-semibold text-brand-green">
              {assignmentCount}
            </span>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onRemove();
              }}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[var(--ba-muted)] hover:bg-black/5 hover:text-brand-danger"
              aria-label="Rimuovi assegnazione"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {contactLine && (
          <p className="mt-0.5 truncate text-[10px] text-[var(--ba-muted)]">
            {contactLine}
          </p>
        )}
        {extraLines.map((line) => (
          <p key={line.label} className="mt-0.5 truncate text-[10px] text-[var(--ba-muted)]">
            <span className="font-medium">{line.label}:</span> {line.value}
          </p>
        ))}
        {availabilitySlots.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {availabilitySlots.slice(0, 3).map((slot) => (
              <span
                key={slot}
                className="max-w-full truncate rounded-full bg-brand-orange/12 px-1.5 py-0.5 text-[9px] font-medium text-brand-orange"
              >
                {slot}
              </span>
            ))}
            {availabilitySlots.length > 3 && (
              <span className="text-[9px] text-[var(--ba-muted)]">+{availabilitySlots.length - 3}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DraggableAssignmentCard({
  assignment,
  participant,
  onRemove,
  assignmentCount,
  isSelected,
  onMobileSelect,
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `assignment:${assignment.id}`,
    data: { type: 'assignment', assignmentId: assignment.id, responseId: assignment.responseId },
  });

  return (
    <div ref={setNodeRef} className={isDragging ? 'opacity-40' : ''}>
      <ParticipantCard
        participant={participant}
        onRemove={() => onRemove(assignment.id)}
        compact
        showGrip
        selected={isSelected}
        assignmentCount={assignmentCount}
        onSelect={onMobileSelect}
        dragHandleProps={{ ...listeners, ...attributes, className: 'cursor-grab active:cursor-grabbing' }}
      />
    </div>
  );
}

function DroppableSlotBody({
  storeId,
  slot,
  children,
  dragParticipant,
  isMobilePickMode,
  onMobileSlotTap,
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `slot-${storeId}-${slot}`,
    data: { type: 'slot', storeId, slot },
  });

  const slotMatch = dragParticipant ? participantMatchesSlot(dragParticipant, slot) : null;

  return (
    <div
      ref={setNodeRef}
      onClick={() => isMobilePickMode && onMobileSlotTap?.(storeId, slot)}
      onKeyDown={
        isMobilePickMode
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onMobileSlotTap?.(storeId, slot);
              }
            }
          : undefined
      }
      role={isMobilePickMode ? 'button' : undefined}
      tabIndex={isMobilePickMode ? 0 : undefined}
      className={cn(
        'min-h-[56px] space-y-1.5 rounded-md border border-dashed p-2 transition-colors',
        isOver && 'border-brand-orange bg-brand-orange/10',
        !isOver && dragParticipant && slotMatch && 'border-brand-green/50 bg-brand-green/10 ring-1 ring-brand-green/30',
        !isOver && dragParticipant && slotMatch === false && 'border-brand-gold/50 bg-brand-gold/10 ring-1 ring-brand-gold/25',
        !isOver && !dragParticipant && 'border-transparent bg-black/[0.02]',
        isMobilePickMode && 'cursor-pointer active:bg-brand-orange/5'
      )}
    >
      {children}
    </div>
  );
}

function SortableSlotBlock({
  storeId,
  slot,
  count,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  dragParticipant,
  isMobilePickMode,
  onMobileSlotTap,
  children,
}) {
  const sortableId = `slot-order:${storeId}:${slot}`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    data: { type: 'slot-order', storeId, slot },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-lg border border-[var(--ba-surface-border)] bg-[var(--ba-surface-bg)] p-2',
        isDragging && 'z-10 opacity-90 shadow-lg ring-2 ring-brand-orange/30'
      )}
    >
      <div className="mb-1.5 flex items-center gap-1">
        <button
          type="button"
          className="cursor-grab touch-none rounded p-0.5 text-[var(--ba-muted)] hover:text-brand-orange active:cursor-grabbing"
          aria-label={`Riordina turno ${slot}`}
          {...listeners}
          {...attributes}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <p className="min-w-0 flex-1 text-[10px] font-bold uppercase tracking-wide text-[var(--ba-muted)]">
          {slot} {count > 0 && `(${count})`}
        </p>
        <div className="flex shrink-0 flex-col">
          <button
            type="button"
            disabled={!canMoveUp}
            onClick={onMoveUp}
            className="rounded p-0.5 text-[var(--ba-muted)] hover:text-brand-orange disabled:opacity-30"
            aria-label="Sposta turno su"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            disabled={!canMoveDown}
            onClick={onMoveDown}
            className="rounded p-0.5 text-[var(--ba-muted)] hover:text-brand-orange disabled:opacity-30"
            aria-label="Sposta turno giù"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <DroppableSlotBody
        storeId={storeId}
        slot={slot}
        dragParticipant={dragParticipant}
        isMobilePickMode={isMobilePickMode}
        onMobileSlotTap={onMobileSlotTap}
      >
        {children}
      </DroppableSlotBody>
    </div>
  );
}

function StoreColumn({
  store,
  globalSlots,
  assignments,
  participants,
  onUpdateStore,
  onRemoveStore,
  onRemoveAssignment,
  onReorderSlot,
  dragParticipant,
  isMobilePickMode,
  onMobileSlotTap,
  onMobileAssignmentPick,
  mobileSelectedAssignmentId,
}) {
  const orderedSlots = getStoreSlotOrder(store, globalSlots);
  const sortableIds = orderedSlots.map((slot) => `slot-order:${store.id}:${slot}`);

  const storeParticipants = participants.filter((p) =>
    assignments.some((a) => a.storeId === store.id && a.responseId === p.responseId)
  );

  const moveSlot = (slot, direction) => {
    const index = orderedSlots.indexOf(slot);
    const target = orderedSlots[index + direction];
    if (target) onReorderSlot(store.id, slot, target);
  };

  return (
    <div className="flex w-72 shrink-0 flex-col overflow-hidden rounded-xl border border-[var(--ba-surface-border)] bg-[var(--ba-surface-bg)]">
      <div className="border-b border-[var(--ba-surface-border)] p-3">
        <div className="mb-2 flex items-start justify-between gap-2">
          <h3 className="font-bold leading-tight">{store.name}</h3>
          <TooltipIconButton
            label="Elimina negozio"
            icon={Trash2}
            variant="destructive"
            onClick={() => onRemoveStore(store.id)}
          />
        </div>

        <div className="space-y-2">
          <div>
            <label className="text-[10px] font-semibold uppercase text-[var(--ba-muted)]">Responsabile</label>
            <select
              className="ba-input mt-0.5 text-xs"
              value={store.leaderResponseId || ''}
              onChange={(e) => onUpdateStore(store.id, { leaderResponseId: e.target.value || null })}
            >
              <option value="">— Nessuno —</option>
              {storeParticipants.map((p) => (
                <option key={p.responseId} value={p.responseId}>{formatParticipantDisplayName(p)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase text-[var(--ba-muted)]">Vice responsabile</label>
            <select
              className="ba-input mt-0.5 text-xs"
              value={store.viceLeaderResponseId || ''}
              onChange={(e) => onUpdateStore(store.id, { viceLeaderResponseId: e.target.value || null })}
            >
              <option value="">— Nessuno —</option>
              {storeParticipants
                .filter((p) => p.responseId !== store.leaderResponseId)
                .map((p) => (
                  <option key={p.responseId} value={p.responseId}>{formatParticipantDisplayName(p)}</option>
                ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase text-[var(--ba-muted)]">Note negozio</label>
            <textarea
              className="ba-input mt-0.5 min-h-[48px] text-xs"
              rows={2}
              value={store.notes || ''}
              onChange={(e) => onUpdateStore(store.id, { notes: e.target.value })}
              placeholder="Note per questo negozio…"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto p-3">
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          {orderedSlots.map((slot, index) => {
            const slotAssignments = getResolvedSlotAssignments(
              store,
              slot,
              assignments,
              participants
            );
            return (
              <SortableSlotBlock
                key={slot}
                storeId={store.id}
                slot={slot}
                count={slotAssignments.length}
                canMoveUp={index > 0}
                canMoveDown={index < orderedSlots.length - 1}
                onMoveUp={() => moveSlot(slot, -1)}
                onMoveDown={() => moveSlot(slot, 1)}
                dragParticipant={dragParticipant}
                isMobilePickMode={isMobilePickMode}
                onMobileSlotTap={onMobileSlotTap}
              >
                {slotAssignments.map((asn) => {
                  const p = participants.find((x) => x.responseId === asn.responseId);
                  if (!p) return null;
                  return (
                    <DraggableAssignmentCard
                      key={asn.id}
                      assignment={asn}
                      participant={p}
                      onRemove={onRemoveAssignment}
                      assignmentCount={countParticipantAssignments(p.responseId, assignments)}
                      isSelected={mobileSelectedAssignmentId === asn.id}
                      onMobileSelect={
                        onMobileAssignmentPick
                          ? () => onMobileAssignmentPick(asn.id, asn.responseId)
                          : undefined
                      }
                    />
                  );
                })}
              </SortableSlotBlock>
            );
          })}
        </SortableContext>
      </div>
    </div>
  );
}

export { ParticipantCard, StoreColumn, DraggableAssignmentCard };
